import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  GridFour, CalendarBlank, ChartLineUp, Calculator, Package, UsersFour,
  ArrowRight, Check, Sparkle, Crown, Star, Lightning, ShieldCheck, Clock
} from "@phosphor-icons/react";

const FEATURES = [
  { icon: GridFour, title: "Live Floor Plan", text: "Drag-and-drop floor builder with real-time table status, smart seat snapping, and turn-time tracking. iPad-optimized." },
  { icon: CalendarBlank, title: "Reservations + Walk-Ins", text: "Auto-tier members, manage waitlists with live wait estimates, and sync table status bidirectionally." },
  { icon: UsersFour, title: "Server Sections", text: "Assign servers to sections, schedule shifts, track covers per server, leaderboards & daily history." },
  { icon: ChartLineUp, title: "Sales & Analytics", text: "Custom sales categories, peak-hour heatmaps, multi-day trends, and end-of-service PDF reports." },
  { icon: Calculator, title: "Accounting & P&L", text: "Track COGS, labor, and overhead. Daily revenue-vs-expense charts, profit margin, COGS%, labor%." },
  { icon: Package, title: "Inventory & Waste", text: "Par levels, stock counts, waste logging by reason. Low-stock alerts and inventory value tracking." },
];

const PLANS = [
  { id: "starter", name: "Starter", price: 49, icon: Star, color: "#81B29A",
    features: ["Up to 5 staff", "Floor plan + Reservations", "Sales tracking", "Basic analytics", "Email support"] },
  { id: "professional", name: "Professional", price: 99, icon: Sparkle, color: "#2F4F4F", popular: true,
    features: ["Unlimited staff", "Everything in Starter", "Inventory tracking", "Accounting & P&L", "Custom categories", "Member CRM with tiers", "Priority support"] },
  { id: "enterprise", name: "Enterprise", price: 199, icon: Crown, color: "#3D405B",
    features: ["Everything in Professional", "Multi-location support", "Custom integrations", "Dedicated success manager", "SLA guarantee", "Custom branding"] },
];

const SOCIAL_PROOF = [
  { metric: "45 min", label: "Avg. saved per service" },
  { metric: "30%", label: "Less waste tracked" },
  { metric: "2.4x", label: "Faster onboarding vs Toast" },
  { metric: "14-day", label: "Free trial, no card required" },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: "#F5F1E8", color: "#1C1917" }} data-testid="landing-page">
      {/* Grain overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04] mix-blend-multiply z-0" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")"
      }} />

      {/* Nav */}
      <nav className="relative z-20 px-6 sm:px-10 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: "#2F4F4F" }}>
            <GridFour size={18} weight="duotone" className="text-white" />
          </div>
          <span className="text-base font-semibold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", letterSpacing: "0.02em" }}>Dining Floor Studio</span>
        </Link>
        <div className="hidden md:flex items-center gap-7">
          <a href="#features" className="text-xs uppercase tracking-widest text-stone-600 hover:text-stone-900 transition-colors">Features</a>
          <a href="#pricing" className="text-xs uppercase tracking-widest text-stone-600 hover:text-stone-900 transition-colors">Pricing</a>
          <a href="#story" className="text-xs uppercase tracking-widest text-stone-600 hover:text-stone-900 transition-colors">Why us</a>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" className="text-xs uppercase tracking-widest font-semibold text-stone-700 hover:text-stone-900 px-3 py-2" data-testid="landing-signin">Sign in</Link>
          <Link to="/login" data-testid="landing-cta-nav">
            <Button size="sm" className="rounded-full px-5" style={{ backgroundColor: "#1C1917", color: "#F5F1E8" }}>Start Free <ArrowRight size={14} className="ml-1.5" weight="bold" /></Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 px-6 sm:px-10 pt-12 pb-20 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-in">
            <Badge className="mb-5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-semibold" data-testid="hero-badge">
              <Lightning size={12} weight="fill" className="mr-1.5" /> Built by restaurant operators
            </Badge>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-normal leading-[0.98] mb-6" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", letterSpacing: "-0.02em" }} data-testid="hero-headline">
              Run your floor<br />
              <span className="italic" style={{ color: "#2F4F4F" }}>like a Michelin pro.</span>
            </h1>
            <p className="text-lg text-stone-600 mb-8 max-w-xl leading-relaxed" data-testid="hero-subheadline">
              Floor plans, reservations, sections, sales, accounting, and inventory — in one elegant tool. Replace Toast + OpenTable + spreadsheets with software that actually feels like a restaurant.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link to="/login" data-testid="hero-cta-primary">
                <Button className="rounded-full px-7 h-12 text-base font-medium shadow-sm" style={{ backgroundColor: "#1C1917", color: "#F5F1E8" }}>
                  Start 14-day free trial <ArrowRight size={16} className="ml-2" weight="bold" />
                </Button>
              </Link>
              <a href="#pricing" data-testid="hero-cta-secondary">
                <Button variant="outline" className="rounded-full px-7 h-12 text-base font-medium border-stone-300 bg-transparent hover:bg-stone-100">
                  See pricing
                </Button>
              </a>
            </div>
            <p className="text-xs text-stone-500 mt-5 flex items-center gap-1.5">
              <ShieldCheck size={14} weight="duotone" /> No credit card required · Cancel anytime · Setup in &lt; 10 min
            </p>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border-4 border-stone-900/5 shadow-2xl" style={{ backgroundColor: "#FFFFFF" }}>
              <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-1.5" style={{ backgroundColor: "#F5F1E8" }}>
                <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                <span className="text-[10px] text-stone-500 ml-3 font-mono">white-columns.app</span>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold tracking-tight">Friday Service · 7:32 PM</h3>
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">LIVE</Badge>
                </div>
                {/* Mini floor plan */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { s: "occ", n: "T1" }, { s: "av", n: "T2" }, { s: "res", n: "T3" }, { s: "occ", n: "T4" },
                    { s: "av", n: "T5" }, { s: "occ", n: "T6" }, { s: "occ", n: "T7" }, { s: "av", n: "T8" },
                    { s: "res", n: "T9" }, { s: "av", n: "10" }, { s: "occ", n: "11" }, { s: "occ", n: "12" },
                  ].map((t, i) => {
                    const colors = { occ: "#E07A5F", res: "#F4A261", av: "#81B29A" };
                    return (
                      <div key={i} className="aspect-square rounded-lg border-2 flex items-center justify-center text-[10px] font-semibold" style={{ borderColor: colors[t.s], backgroundColor: colors[t.s] + "15", color: colors[t.s] }}>{t.n}</div>
                    );
                  })}
                </div>
                <div className="flex gap-2 pt-2">
                  <div className="flex-1 rounded-lg p-2.5" style={{ backgroundColor: "#F5F1E8" }}>
                    <p className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">Covers</p>
                    <p className="text-lg font-semibold" style={{ color: "#2F4F4F" }}>87</p>
                  </div>
                  <div className="flex-1 rounded-lg p-2.5" style={{ backgroundColor: "#F5F1E8" }}>
                    <p className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">Revenue</p>
                    <p className="text-lg font-semibold" style={{ color: "#81B29A" }}>$5,420</p>
                  </div>
                  <div className="flex-1 rounded-lg p-2.5" style={{ backgroundColor: "#F5F1E8" }}>
                    <p className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">Wait</p>
                    <p className="text-lg font-semibold" style={{ color: "#E07A5F" }}>22m</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Floating notification card */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl border border-stone-200 p-3 max-w-[200px] hidden md:block">
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: "#81B29A20" }}>
                  <Check size={14} weight="bold" style={{ color: "#81B29A" }} />
                </div>
                <div>
                  <p className="text-[11px] font-semibold">Smith party seated</p>
                  <p className="text-[10px] text-stone-500">Table 7 · Maria S.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social proof strip */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 border-y border-stone-300/50 py-8">
          {SOCIAL_PROOF.map((s, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <p className="text-3xl sm:text-4xl font-normal mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2F4F4F" }}>{s.metric}</p>
              <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 px-6 sm:px-10 py-20 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-14">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#2F4F4F" }}>What's inside</p>
          <h2 className="text-4xl sm:text-5xl font-normal mb-4 leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.02em" }}>
            Six tools that <em className="not-italic underline decoration-amber-400 decoration-4 underline-offset-4">replace twelve.</em>
          </h2>
          <p className="text-stone-600 text-lg leading-relaxed">
            We talked to 40+ restaurant owners. They were paying for Toast, OpenTable, Resy, MarketMan, QuickBooks, and a chef's notebook. We built one tool that does it all — and feels good to use.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-300/40 rounded-2xl overflow-hidden border border-stone-300/40">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="p-7 transition-all duration-300 hover:translate-y-[-2px]" style={{ backgroundColor: "#F5F1E8" }} data-testid={`feature-${i}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: "#2F4F4F" }}>
                  <Icon size={22} weight="duotone" className="text-amber-300" />
                </div>
                <h3 className="text-xl font-semibold mb-2 tracking-tight" style={{ color: "#1C1917" }}>{f.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Story / Why us */}
      <section id="story" className="relative z-10 px-6 sm:px-10 py-20" style={{ backgroundColor: "#1C1917" }}>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 text-amber-300">Why us</p>
            <h2 className="text-4xl sm:text-5xl font-normal mb-6 leading-[1.05] text-white" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.02em" }}>
              Software shouldn't fight you<br />during the dinner rush.
            </h2>
            <div className="space-y-5 text-stone-300 text-base leading-relaxed">
              <p>Most restaurant tech is built by people who've never bussed a table. It's bloated, expensive, and forces you to think like the software instead of like a restaurant.</p>
              <p>Dining Floor Studio is different. The floor plan looks like a <em className="not-italic text-amber-300">floor plan</em>. Reservations sync to tables automatically. Tabs feel like tabs.</p>
              <p>It's the tool we wished we had — and now you can have it.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {["iPad-first", "WebSockets sync", "PDF reports", "CSV exports", "Multi-tenant SaaS", "Real-time chat"].map((tag) => (
                <span key={tag} className="text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full border border-amber-300/30 text-amber-200">{tag}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "10 min", l: "Average setup time" },
              { v: "99.9%", l: "Uptime SLA" },
              { v: "0¢", l: "Per-cover fees" },
              { v: "24/7", l: "Live support" },
            ].map((s, i) => (
              <div key={i} className="p-7 rounded-2xl border border-stone-700 backdrop-blur-sm" style={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
                <p className="text-4xl font-normal mb-1 text-amber-300" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.v}</p>
                <p className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 px-6 sm:px-10 py-20 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#2F4F4F" }}>Pricing</p>
          <h2 className="text-4xl sm:text-5xl font-normal mb-4 leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.02em" }}>
            Simple plans. <em className="not-italic" style={{ color: "#2F4F4F" }}>Real value.</em>
          </h2>
          <p className="text-stone-600 text-lg">No per-cover fees. No surprise charges. Cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.id} className={`relative rounded-2xl p-7 transition-all hover:translate-y-[-4px] ${p.popular ? "shadow-2xl" : "shadow-sm"}`} style={{ backgroundColor: p.popular ? "#1C1917" : "#FFFFFF", border: p.popular ? "none" : "1px solid #E7E5E4" }} data-testid={`plan-card-${p.id}`}>
                {p.popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-300 text-stone-900 text-[10px] uppercase tracking-widest font-semibold px-3 py-1">Most popular</Badge>}
                <div className="flex items-center gap-2 mb-4">
                  <Icon size={20} weight="duotone" style={{ color: p.popular ? "#FCD34D" : p.color }} />
                  <h3 className="text-base font-semibold uppercase tracking-widest" style={{ color: p.popular ? "#FCD34D" : p.color }}>{p.name}</h3>
                </div>
                <div className="mb-5">
                  <span className="text-5xl font-normal" style={{ fontFamily: "'Cormorant Garamond', serif", color: p.popular ? "#FFFFFF" : "#1C1917" }}>${p.price}</span>
                  <span className={`ml-1 text-sm ${p.popular ? "text-stone-400" : "text-stone-500"}`}>/mo</span>
                </div>
                <ul className="space-y-3 mb-7 min-h-[200px]">
                  {p.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${p.popular ? "text-stone-300" : "text-stone-600"}`}>
                      <Check size={16} weight="bold" className="mt-0.5 flex-shrink-0" style={{ color: p.popular ? "#FCD34D" : p.color }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/login" data-testid={`plan-cta-${p.id}`}>
                  <Button className="w-full rounded-full h-11" style={{ backgroundColor: p.popular ? "#FCD34D" : p.color, color: p.popular ? "#1C1917" : "#FFFFFF" }}>Start free trial</Button>
                </Link>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-stone-500 mt-8 flex items-center justify-center gap-1.5">
          <Clock size={13} weight="duotone" /> 14-day free trial on every plan · No credit card required
        </p>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-6 sm:px-10 py-24">
        <div className="max-w-4xl mx-auto text-center rounded-3xl p-12 sm:p-16 relative overflow-hidden" style={{ backgroundColor: "#2F4F4F" }}>
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "radial-gradient(circle at 20% 30%, #FCD34D 0%, transparent 50%), radial-gradient(circle at 80% 70%, #E07A5F 0%, transparent 50%)"
          }} />
          <div className="relative">
            <h2 className="text-4xl sm:text-5xl font-normal mb-4 text-white leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.02em" }}>
              Stop juggling six logins.<br />
              <span className="italic text-amber-300">Run your floor in one.</span>
            </h2>
            <p className="text-stone-300 text-lg mb-8 max-w-xl mx-auto">14 days free. No credit card. If it's not the best restaurant tool you've used, leave.</p>
            <Link to="/login" data-testid="final-cta">
              <Button className="rounded-full px-8 h-12 text-base font-medium" style={{ backgroundColor: "#FCD34D", color: "#1C1917" }}>
                Start your trial <ArrowRight size={16} className="ml-2" weight="bold" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 sm:px-10 py-10 border-t border-stone-300/50 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: "#2F4F4F" }}>
              <GridFour size={14} weight="duotone" className="text-white" />
            </div>
            <span className="text-xs text-stone-600 font-semibold">Dining Floor Studio</span>
          </div>
          <div className="flex items-center gap-5 text-[11px] uppercase tracking-widest text-stone-500">
            <Link to="/privacy" className="hover:text-stone-800 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-800 transition-colors">Terms</Link>
            <a href="mailto:creator@diningfloorstudio.com" className="hover:text-stone-800 transition-colors">Contact</a>
          </div>
          <p className="text-[10px] text-stone-500">© 2026 Dining Floor Studio</p>
        </div>
      </footer>
    </div>
  );
}
